import React, { useState, useEffect } from 'react';
import {
  Cog6ToothIcon,
  ShieldCheckIcon,
  BellIcon,
  EnvelopeIcon,
  CreditCardIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  DocumentArrowDownIcon, // Replaced SaveIcon with DocumentArrowDownIcon
  CheckCircleIcon // Added for success messages
} from '@heroicons/react/24/outline';
import adminApi from '../../api/adminApi';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    // General Settings
    siteName: 'GenZEvaluation',
    siteDescription: 'Coding Competition Platform',
    contactEmail: 'admin@genzevaluation.com',
    supportEmail: 'support@genzevaluation.com',
    
    // Security Settings
    maintenanceMode: false,
    registrationEnabled: true,
    emailVerificationRequired: true,
    
    // Payment Settings
    currency: 'INR',
    razorpayKey: '',
    stripeKey: '',
    
    // Email Settings
    smtpHost: '',
    smtpPort: '',
    smtpUser: '',
    smtpPassword: '',
    
    // Notification Settings
    emailNotifications: true,
    newUserNotification: true,
    paymentNotification: true,
    competitionNotification: true
  });

  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
  const fetchSettings = async () => {
    try {
      const data = await adminApi.getSettings(); 
      
      // We map the database JSON objects back into your local state
      setSettings(prev => ({
        ...prev,
        ...(data.site_config || {}),
        ...(data.payment_config || {}),
        // Ensure booleans are treated as booleans
        maintenanceMode: data.site_config?.maintenanceMode ?? false,
        registrationEnabled: data.site_config?.registrationEnabled ?? true
      }));
    } catch (error) {
      console.error("Error loading settings from DB:", error);
    }
  };
  fetchSettings();
}, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveSettings = async () => {
  setLoading(true);
  try {
    // 1. General & Security
    await adminApi.updateSettings({
      key: 'site_config',
      value: {
        siteName: settings.siteName,
        siteDescription: settings.siteDescription,
        contactEmail: settings.contactEmail,
        supportEmail: settings.supportEmail,
        maintenanceMode: settings.maintenanceMode,
        registrationEnabled: settings.registrationEnabled,
        emailVerificationRequired: settings.emailVerificationRequired
      }
    });

    // 2. Payment
    await adminApi.updateSettings({
      key: 'payment_config',
      value: {
        currency: settings.currency,
        razorpayKey: settings.razorpayKey,
        stripeKey: settings.stripeKey
      }
    });

    // 3. Email (The SMTP details you were confused about)
    await adminApi.updateSettings({
      key: 'email_config',
      value: {
        smtpHost: settings.smtpHost,
        smtpPort: settings.smtpPort,
        smtpUser: settings.smtpUser,
        smtpPassword: settings.smtpPassword
      }
    });

    // 4. Notifications
    await adminApi.updateSettings({
      key: 'notification_config',
      value: {
        emailNotifications: settings.emailNotifications,
        newUserNotification: settings.newUserNotification,
        paymentNotification: settings.paymentNotification,
        competitionNotification: settings.competitionNotification
      }
    });

    setSaveMessage('✅ All settings synced to database!');
  } catch (error) {
    console.error(error);
    setSaveMessage('❌ Failed to save to server');
  } finally {
    setLoading(false);
    setTimeout(() => setSaveMessage(''), 3000);
  }
};

  const handleResetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults?')) {
      setSettings({
        siteName: 'GenZEvaluation',
        siteDescription: 'Coding Competition Platform',
        contactEmail: 'admin@genzevaluation.com',
        supportEmail: 'support@genzevaluation.com',
        maintenanceMode: false,
        registrationEnabled: true,
        emailVerificationRequired: true,
        currency: 'INR',
        razorpayKey: '',
        stripeKey: '',
        smtpHost: '',
        smtpPort: '',
        smtpUser: '',
        smtpPassword: '',
        emailNotifications: true,
        newUserNotification: true,
        paymentNotification: true,
        competitionNotification: true
      });
      setSaveMessage('✅ Settings reset to defaults!');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const tabs = [
    { id: 'general', name: 'General', icon: Cog6ToothIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon },
    { id: 'payment', name: 'Payment', icon: CreditCardIcon },
    { id: 'email', name: 'Email', icon: EnvelopeIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon }
  ];

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Platform Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Site Name *
            </label>
            <input
              type="text"
              name="siteName"
              value={settings.siteName}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Contact Email *
            </label>
            <input
              type="email"
              name="contactEmail"
              value={settings.contactEmail}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              required
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Site Description
            </label>
            <textarea
              name="siteDescription"
              value={settings.siteDescription}
              onChange={handleInputChange}
              rows="3"
              className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
            />
          </div>
        </div>
      </div>
      
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Support Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Support Email
            </label>
            <input
              type="email"
              name="supportEmail"
              value={settings.supportEmail}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div className="bg-red-900/20 border border-red-700/30 rounded-xl p-4">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
          <div>
            <h4 className="font-medium text-white mb-1">Security Settings</h4>
            <p className="text-sm text-gray-300">These settings affect platform security and access.</p>
          </div>
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg border border-gray-700">
          <div>
            <h4 className="font-medium text-white">Maintenance Mode</h4>
            <p className="text-sm text-gray-400">Temporarily disable platform access</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="maintenanceMode"
              checked={settings.maintenanceMode}
              onChange={handleInputChange}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg border border-gray-700">
          <div>
            <h4 className="font-medium text-white">New Registration</h4>
            <p className="text-sm text-gray-400">Allow new users to register</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="registrationEnabled"
              checked={settings.registrationEnabled}
              onChange={handleInputChange}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg border border-gray-700">
          <div>
            <h4 className="font-medium text-white">Email Verification</h4>
            <p className="text-sm text-gray-400">Require email verification for new accounts</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="emailVerificationRequired"
              checked={settings.emailVerificationRequired}
              onChange={handleInputChange}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
          </label>
        </div>
      </div>
    </div>
  );

  const renderPaymentSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-white mb-4">Payment Gateway</h3>
        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Currency
            </label>
            <select
              name="currency"
              value={settings.currency}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            >
              <option value="INR">Indian Rupee (₹)</option>
              <option value="USD">US Dollar ($)</option>
              <option value="EUR">Euro (€)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Razorpay Key ID
            </label>
            <input
              type="password"
              name="razorpayKey"
              value={settings.razorpayKey}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              placeholder="rzp_test_xxxxxxxxxxxx"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Stripe Secret Key
            </label>
            <input
              type="password"
              name="stripeKey"
              value={settings.stripeKey}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              placeholder="sk_test_xxxxxxxxxxxx"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderEmailSettings = () => (
  <div className="space-y-6">
    <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-6">
      <div className="flex items-start">
        <EnvelopeIcon className="h-5 w-5 text-blue-400 mr-3 mt-0.5" />
        <div>
          <h4 className="font-medium text-white mb-1">Email Service: Resend</h4>
          <p className="text-sm text-gray-300">
            Emails are sent via Resend API. The API key is configured 
            securely in the server environment variables.
          </p>
          <p className="text-xs text-gray-500 mt-2">
            To change the API key, update <code className="text-blue-400">RESEND_API_KEY</code> 
            in your Render backend environment variables.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-green-900/20 border border-green-700/30 rounded-xl p-4">
      <div className="flex items-center gap-2">
        <CheckCircleIcon className="h-5 w-5 text-green-400" />
        <span className="text-green-400 text-sm font-medium">
          Email service is active — 3,000 free emails/month via Resend
        </span>
      </div>
    </div>
  </div>
);

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg border border-gray-700">
          <div>
            <h4 className="font-medium text-white">Email Notifications</h4>
            <p className="text-sm text-gray-400">Enable all email notifications</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="emailNotifications"
              checked={settings.emailNotifications}
              onChange={handleInputChange}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg border border-gray-700">
          <div>
            <h4 className="font-medium text-white">New User Registration</h4>
            <p className="text-sm text-gray-400">Notify admin when new user registers</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="newUserNotification"
              checked={settings.newUserNotification}
              onChange={handleInputChange}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg border border-gray-700">
          <div>
            <h4 className="font-medium text-white">Payment Notifications</h4>
            <p className="text-sm text-gray-400">Notify admin for successful payments</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="paymentNotification"
              checked={settings.paymentNotification}
              onChange={handleInputChange}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
        
        <div className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg border border-gray-700">
          <div>
            <h4 className="font-medium text-white">Competition Notifications</h4>
            <p className="text-sm text-gray-400">Notify admin for competition updates</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="competitionNotification"
              checked={settings.competitionNotification}
              onChange={handleInputChange}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center">
              <Cog6ToothIcon className="h-7 w-7 mr-3 text-gray-400" />
              Platform Settings
            </h1>
            <p className="text-gray-400 mt-1">Configure and customize your platform</p>
          </div>
        </div>
      </div>

      {/* Settings Content */}
      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-gray-700">
          <nav className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'text-white border-b-2 border-blue-500 bg-gray-900/30'
                    : 'text-gray-400 hover:text-gray-300 hover:bg-gray-900/10'
                }`}
              >
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'general' && renderGeneralSettings()}
          {activeTab === 'security' && renderSecuritySettings()}
          {activeTab === 'payment' && renderPaymentSettings()}
          {activeTab === 'email' && renderEmailSettings()}
          {activeTab === 'notifications' && renderNotificationSettings()}
        </div>

        {/* Save Bar */}
        <div className="border-t border-gray-700 p-6 bg-gray-900/30">
          <div className="flex items-center justify-between">
            <div>
              {saveMessage && (
                <div className="text-green-400 text-sm flex items-center">
                  <CheckCircleIcon className="h-4 w-4 mr-2" /> {/* Changed to CheckCircleIcon */}
                  {saveMessage}
                </div>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleResetToDefaults}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg border border-gray-600 transition-colors flex items-center"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Reset to Defaults
              </button>
              
              <button
                onClick={handleSaveSettings}
                disabled={loading}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <DocumentArrowDownIcon className="h-4 w-4 mr-2" /> {/* Changed to DocumentArrowDownIcon */}
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-xl p-4">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-3 mt-0.5" />
          <div>
            <h4 className="font-medium text-white mb-1">Important Notice</h4>
            <p className="text-sm text-gray-300">
              Changes to these settings will affect the entire platform. Please test changes in a staging environment first.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;